import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Store, Utensils, ShoppingCart, Scissors, Building2, Check, Settings, Settings2, Printer, MessageCircle, Mail, Leaf, RotateCcw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const ICONS = { restaurant: Utensils, supermarket: ShoppingCart, salon: Scissors, general_store: Store };
const COLORS = { restaurant: 'bg-orange-50 border-orange-200 text-orange-800', supermarket: 'bg-info/10 border-info/30 text-info', salon: 'bg-pink-50 border-pink-200 text-pink-800', general_store: 'bg-success/10 border-success/30 text-success' };

export default function POSConfigPage() {
  const { t } = useLanguage();
  const [industries, setIndustries] = useState([]);
  const [branchConfigs, setBranchConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('restaurant');
  const [billSettings, setBillSettings] = useState([]);
  const [savingBill, setSavingBill] = useState(null);

  const fetchData = async () => {
    try {
      const [indRes, brRes, billRes] = await Promise.all([
        api.get('/pos/industries'),
        api.get('/pos/branch-config'),
        api.get('/pos/bill-delivery-settings'),
      ]);
      setIndustries(indRes.data);
      setBranchConfigs(brRes.data);
      setBillSettings(billRes.data || []);
    } catch (err) {
      toast.error('Failed to load POS configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleBillOpt = (branchId, key) => {
    setBillSettings(prev => prev.map(b =>
      b.branch_id === branchId ? { ...b, options: { ...b.options, [key]: !b.options[key] } } : b
    ));
  };

  const saveBillSettings = async (bc, applyAll) => {
    setSavingBill(bc.branch_id + (applyAll ? '-all' : ''));
    try {
      await api.put(`/pos/bill-delivery-settings/${bc.branch_id}`, { options: bc.options, apply_all: applyAll });
      toast.success(applyAll ? 'Applied to all branches' : `Saved for ${bc.branch_name}`);
      fetchData();
    } catch {
      toast.error('Failed to save bill options');
    } finally {
      setSavingBill(null);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedBranch) return;
    try {
      await api.put(`/pos/branch-config/${selectedBranch.branch_id}`, { industry: selectedIndustry });
      toast.success('POS type configured successfully');
      setShowConfigDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save configuration');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pos-config-page">
        <div>
          <div className="flex items-center gap-4">

            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-500 to-zinc-600 flex items-center justify-center shadow-lg shadow-stone-200/50">

              <Settings size={24} className="text-white" />

            </div>

            <div>

              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('pos_config_title') || 'POS Configuration'}</h1>

              <p className="text-sm text-muted-foreground mt-0.5">POS terminal configuration</p>

            </div>

          </div>
          <p className="text-muted-foreground text-sm mt-1">Configure POS type per branch. Each branch can have a different business type.</p>
        </div>

        {/* Industry Types */}
        <div>
          <h2 className="text-sm font-semibold text-foreground mb-3">Available POS Types</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((ind) => {
              const Icon = ICONS[ind.id] || Store;
              const colorClass = COLORS[ind.id] || 'bg-muted/50 border-border';
              return (
                <Card key={ind.id} className={`border-2 ${colorClass}`} data-testid={`industry-${ind.id}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-white/50"><Icon size={20} /></div>
                      <div className="font-medium text-sm">{ind.name}</div>
                    </div>
                    <p className="text-xs opacity-70 mb-3">{ind.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {ind.features?.slice(0, 5).map((f) => (
                        <Badge key={f} variant="outline" className="text-[9px] capitalize bg-white/50">{f.replace(/_/g, ' ')}</Badge>
                      ))}
                      {ind.features?.length > 5 && <Badge variant="outline" className="text-[9px] bg-white/50">+{ind.features.length - 5} more</Badge>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Branch Configurations */}
        <div>
          <h2 className="text-sm font-semibold text-foreground mb-3">Branch POS Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {branchConfigs.map((bc) => {
              const Icon = ICONS[bc.industry] || Store;
              const ind = industries.find(i => i.id === bc.industry);
              return (
                <Card key={bc.branch_id} className="hover:shadow-md transition-all" data-testid={`branch-pos-${bc.branch_id}`}>
                  <CardContent className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-lg"><Building2 size={18} className="text-muted-foreground" /></div>
                      <div>
                        <div className="font-medium text-sm">{bc.branch_name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Icon size={12} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{ind?.name || bc.industry}</span>
                          {bc.configured && <Badge variant="default" className="text-[9px]"><Check size={8} className="mr-0.5" /> Configured</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { setSelectedBranch(bc); setSelectedIndustry(bc.industry || 'restaurant'); setShowConfigDialog(true); }} data-testid={`config-btn-${bc.branch_id}`}>
                      <Settings2 size={14} className="mr-1" /> Configure
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {branchConfigs.length === 0 && <div className="text-sm text-muted-foreground col-span-2 text-center py-8">No branches found. Create branches first.</div>}
          </div>
        </div>

        {/* iter746 — Bill Delivery Options per branch */}
        <div data-testid="bill-delivery-section">
          <h2 className="text-sm font-semibold text-foreground mb-1">Bill Delivery Options</h2>
          <p className="text-xs text-muted-foreground mb-3">Choose which bill-output options cashiers can use at each branch: Print, WhatsApp, Email, Go Green (paperless), and Reprint &amp; Resend.</p>
          <div className="grid grid-cols-1 gap-3">
            {(() => {
              const BILL_OPTS = [
                { key: 'print', label: 'Print', Icon: Printer },
                { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
                { key: 'email', label: 'Email', Icon: Mail },
                { key: 'go_green', label: 'Go Green', Icon: Leaf },
                { key: 'reprint_resend', label: 'Reprint & Resend', Icon: RotateCcw },
              ];
              return billSettings.map((bc) => (
                <Card key={bc.branch_id} data-testid={`bill-opts-${bc.branch_id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 size={16} className="text-muted-foreground" />
                      <span className="font-medium text-sm">{bc.branch_name}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {BILL_OPTS.map(({ key, label, Icon }) => {
                        const on = bc.options?.[key] !== false;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => toggleBillOpt(bc.branch_id, key)}
                            className={`flex items-center gap-1.5 px-3 h-9 rounded-lg border-2 text-xs font-medium transition-all ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-stone-500 border-stone-300'}`}
                            data-testid={`bill-opt-${bc.branch_id}-${key}`}
                          >
                            <Icon size={14} /> {label} {on ? '· On' : '· Off'}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={() => saveBillSettings(bc, false)} disabled={savingBill === bc.branch_id} data-testid={`bill-save-${bc.branch_id}`}>
                        {savingBill === bc.branch_id ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => saveBillSettings(bc, true)} disabled={savingBill === bc.branch_id + '-all'} data-testid={`bill-save-all-${bc.branch_id}`}>
                        {savingBill === bc.branch_id + '-all' ? 'Applying…' : 'Apply to all branches'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ));
            })()}
            {billSettings.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No branches found.</div>}
          </div>
        </div>

        {/* Config Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent data-testid="config-dialog">
            <DialogHeader><DialogTitle>Configure POS - {selectedBranch?.branch_name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium mb-2">Select POS Type</div>
                <div className="grid grid-cols-2 gap-3">
                  {industries.map((ind) => {
                    const Icon = ICONS[ind.id] || Store;
                    const isSelected = selectedIndustry === ind.id;
                    return (
                      <div
                        key={ind.id}
                        className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-border'}`}
                        onClick={() => setSelectedIndustry(ind.id)}
                        data-testid={`select-${ind.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={16} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />
                          <span className="text-sm font-medium">{ind.name}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">{ind.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button onClick={handleSaveConfig} className="w-full" data-testid="save-config-btn">Save Configuration</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
